import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { TemplateLocaleInput, TemplateLocalesResponse } from 'modules/graphql/types';
import { addNegativeToast } from 'modules/toastContainer';
import noOp from 'utils/noop';
import { isEmpty } from '@iterable/utils';
import { Locale } from 'modules/domains/template';

import { updateDefaultLocaleMutation } from 'api/queries';

import { GqlUpdateDefaultLocaleFailureTypes } from 'modules/templateEditor/common';
import { getErrorMessage } from '../common/utils';

export type UpdateDefaultLocaleRequest = {
  templateId: number;
  locale: TemplateLocaleInput;
};

export type UpdateDefaultLocaleResponse = {
  changeTemplateDefaultLocale: TemplateLocalesResponse;
};

type SuccessResponse = {
  templateId: number;
  defaultLocale: Pick<Locale, 'tag'>;
};

type UpdateDefaultLocaleProps = {
  onCompleted?: () => void;
  onError?: (errors: string[]) => void;
  onSuccess?: (resp: SuccessResponse) => void;
};

export function useUpdateDefaultLocale({
  onCompleted = noOp,
  onError = noOp,
  onSuccess = noOp,
}: UpdateDefaultLocaleProps) {
  const dispatch = useDispatch();

  const [updateDefaultLocale, { data, loading, error, called }] = useMutation<
    UpdateDefaultLocaleResponse,
    UpdateDefaultLocaleRequest
  >(updateDefaultLocaleMutation);

  useEffect(() => {
    if (!called) return;

    if (!loading && error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!loading && data?.changeTemplateDefaultLocale) {
      const { defaultLocale, templateId, failures } = data.changeTemplateDefaultLocale;

      const gqlfailures =
        (Array.isArray(failures) || failures === null ? failures : [failures]) ?? [];

      const errorMessages: string[] = [];

      gqlfailures.forEach((errorObj) => {
        switch (errorObj.__typename) {
          case GqlUpdateDefaultLocaleFailureTypes.NOT_FOUND:
            errorMessages.push(`Error: ${errorObj.locale} is not found. Please try again.`);
            break;
          case GqlUpdateDefaultLocaleFailureTypes.LOCALES_DISABLED:
            errorMessages.push(
              `Error: You do not have permission to adjust the default locale. Visit the project settings page for more information.`,
            );
            break;
          default:
            errorMessages.push(getErrorMessage(errorObj));
            break;
        }
      });

      if (templateId && defaultLocale) {
        onSuccess({ defaultLocale, templateId });
      } else if (!isEmpty(errorMessages)) {
        onError(errorMessages);
      }
    }
    onCompleted();
  }, [loading]);
  return [updateDefaultLocale, data] as const;
}
